import { ApolloError, useMutation } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CANCEL_CFDI_MUTATION } from './invoicing.gql';
import type { Invoice } from './types';

import { CFDI_CANCEL_MOTIVO_TO_GQL } from '@/shared/graphql/enums';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface CancelCfdiDialogProps {
  invoice: Invoice;
  on_close: () => void;
  on_done: () => void | Promise<void>;
  on_error: (msg: string | null) => void;
}

const MOTIVOS = ['01', '02', '03', '04'] as const;

export function CancelCfdiDialog({
  invoice,
  on_close,
  on_done,
  on_error,
}: CancelCfdiDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [motivo, setMotivo] = useState<typeof MOTIVOS[number]>('02');
  const [folio_sustitucion, setFolioSustitucion] = useState('');
  const [cancelCfdi, { loading }] = useMutation(CANCEL_CFDI_MUTATION);

  const requires_sustitucion = motivo === '01';
  const can_submit = !loading && (!requires_sustitucion || folio_sustitucion.trim().length > 0);

  const onClickSubmit = async (): Promise<void> => {
    if (!can_submit) return;
    on_error(null);
    const idempotency_key =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      await cancelCfdi({
        variables: {
          input: {
            invoice_id: invoice.id,
            motivo: CFDI_CANCEL_MOTIVO_TO_GQL[motivo],
            folio_sustitucion: folio_sustitucion.trim() || null,
          },
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        context: { headers: { 'Idempotency-Key': idempotency_key } },
      });
      await on_done();
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        if (code.startsWith('invoice_not_cancellable'))
          return on_error(t('invoicing.error_not_cancellable'));
        if (code.startsWith('pac_error'))
          return on_error(`${t('invoicing.error_pac')}: ${code.slice('pac_error:'.length)}`);
        return on_error(code);
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="font-display tracking-tight">
            {t('invoicing.cancel_title')}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {t('invoicing.cancel_hint', { uuid: invoice.uuid })}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>{t('invoicing.cancel_motivo')}</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value as typeof MOTIVOS[number])}
              disabled={loading}
            >
              {MOTIVOS.map((m) => (
                <option key={m} value={m}>
                  {t(`invoicing.motivo_${m}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>
              {t('invoicing.cancel_folio_sustitucion')}
              {requires_sustitucion ? ' *' : ''}
            </Label>
            <Input
              value={folio_sustitucion}
              onChange={(e) => setFolioSustitucion(e.target.value)}
              disabled={loading}
              placeholder={t('invoicing.cancel_folio_sustitucion_placeholder')}
            />
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {loading ? t('common.loading') : t('invoicing.cancel_confirm')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
